import type {
  ApprovalDecision,
  ApprovalRequestRecord,
  CreateApprovalRequestResponse,
  CustomerTransferApprovalPayload,
  DecideApprovalRequestResponse,
  ListApprovalRequestsResponse,
  ListCustomerTransferCandidatesResponse,
} from '@nadoutong/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed client + React Query hooks for `/v1/ops/approvals/*` — the
 * approval-center surface (epic #1030 E1-S3, #1039) plus the customer
 * transfer/release pilot's requester-side hooks (E1-S5, #1041).
 *
 * Server-side scoping is the contract (E1-S2, #1038): the queue is
 * pinned to `status=pending`, kind-filtered to the session's approver
 * roles, and brand-scoped from the sales profile; `mine` is row-level
 * requester = session user. The client never sends scope parameters.
 *
 * All queries share the `['approvals']` root key so a decide mutation
 * invalidates the queue list, the my-submissions list, AND the shell
 * pending-count chip together — approving from the queue must decrement
 * the chip within the same render cycle (#1039 AC 1).
 *
 * Transfer-pilot endpoints (#1041):
 *
 *   - `POST /v1/ops/approvals` — submit a `customer_transfer` request for
 *     an owned customer; ops-api stamps the brand server-side and rejects
 *     non-owners fail-closed.
 *   - `GET /v1/ops/approvals/mine?status=pending&kind=customer_transfer`
 *     — the requester's open transfer requests, used for the pending
 *     chip on Customer 360 and workspace rows. Row-level by design: the
 *     indicator marks MY in-flight submissions (the approver-side queue
 *     is E1-S3's approval center).
 *   - `GET /v1/ops/v2/customer-manager/transfer-candidates` — same-brand
 *     transfer targets for the dialog picker; sales sessions are pinned
 *     to their profile brand, admin sessions pass the customer's brand.
 */

const BASE_PATH = '/v1/ops/approvals';
const TRANSFER_CANDIDATES_PATH = '/v1/ops/v2/customer-manager/transfer-candidates';

export const APPROVALS_ROOT_KEY = ['approvals'] as const;
export const APPROVALS_QUEUE_COUNT_KEY = ['approvals', 'queue-count'] as const;

/** 30s polling, following the shell chip precedent (vps / cron-runs). */
export const APPROVALS_POLL_INTERVAL_MS = 30_000;

/** Matches the platform list MAX_LIST_LIMIT — one page covers a book. */
const PENDING_TRANSFER_LIMIT = 200;

export interface ApprovalListScope {
  readonly limit: number;
  readonly offset: number;
}

function listPath(base: string, scope: ApprovalListScope): string {
  const params = new URLSearchParams();
  params.set('limit', String(scope.limit));
  params.set('offset', String(scope.offset));
  return `${base}?${params.toString()}`;
}

export function approvalQueueQueryKey(
  scope: ApprovalListScope,
): readonly ['approvals', 'queue', ApprovalListScope] {
  return ['approvals', 'queue', scope] as const;
}

export function myApprovalsQueryKey(
  scope: ApprovalListScope,
): readonly ['approvals', 'mine', ApprovalListScope] {
  return ['approvals', 'mine', scope] as const;
}

export async function listApprovalQueue(
  scope: ApprovalListScope,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListApprovalRequestsResponse> {
  return opsApiRequest<ListApprovalRequestsResponse>(
    listPath(`${BASE_PATH}/queue`, scope),
    { signal },
    fetchImpl,
  );
}

export async function listMyApprovals(
  scope: ApprovalListScope,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListApprovalRequestsResponse> {
  return opsApiRequest<ListApprovalRequestsResponse>(
    listPath(`${BASE_PATH}/mine`, scope),
    { signal },
    fetchImpl,
  );
}

/**
 * Approver queue (pending only, server-scoped). Polls so a request
 * submitted elsewhere appears within one 30s cycle (#1039 AC 1).
 * Callers gate `enabled` on approver rights — non-approvers must not
 * fire the request at all (it would 403).
 */
export function useApprovalQueue(
  scope: ApprovalListScope,
  enabled: boolean,
): UseQueryResult<ListApprovalRequestsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: approvalQueueQueryKey(scope),
    queryFn: ({ signal }) => listApprovalQueue(scope, fetchImpl, signal),
    enabled,
    refetchInterval: APPROVALS_POLL_INTERVAL_MS,
  });
}

/**
 * Pending-count for the shell chip: `total` from a `limit=1` queue
 * page keeps the polled payload minimal. Quiet contract mirrors the
 * RiskChip — callers render nothing while loading or on error (e.g. a
 * lead without a sales profile receives 403 `SALES_PROFILE_NOT_FOUND`).
 */
export function useApprovalQueuePendingCount(enabled: boolean): UseQueryResult<number> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: APPROVALS_QUEUE_COUNT_KEY,
    queryFn: async ({ signal }) => {
      const response = await listApprovalQueue({ limit: 1, offset: 0 }, fetchImpl, signal);
      return response.total;
    },
    enabled,
    refetchInterval: APPROVALS_POLL_INTERVAL_MS,
  });
}

/** My submissions (row-level: requester = session user, all statuses). */
export function useMyApprovals(
  scope: ApprovalListScope,
  enabled: boolean,
): UseQueryResult<ListApprovalRequestsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: myApprovalsQueryKey(scope),
    queryFn: ({ signal }) => listMyApprovals(scope, fetchImpl, signal),
    enabled,
  });
}

export interface DecideApprovalInput {
  readonly id: string;
  readonly decision: ApprovalDecision;
  readonly note: string | null;
}

/**
 * Approve / reject one pending request. Invalidation runs `onSettled`
 * (not only on success) so losing the decide race — 409
 * `APPROVAL_REQUEST_NOT_PENDING` — also refreshes the queue and the
 * chip: the row leaves the pending set either way (#1039 AC 3).
 */
export function useDecideApproval(): UseMutationResult<
  DecideApprovalRequestResponse,
  Error,
  DecideApprovalInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, note }) =>
      opsApiRequest<DecideApprovalRequestResponse>(
        `${BASE_PATH}/${encodeURIComponent(id)}/decide`,
        { method: 'POST', body: { decision, note } },
        fetchImpl,
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: APPROVALS_ROOT_KEY });
    },
  });
}

export function myPendingCustomerTransfersQueryKey(): readonly [
  'approvals',
  'mine',
  'customer_transfer',
  'pending',
] {
  return ['approvals', 'mine', 'customer_transfer', 'pending'] as const;
}

export async function listMyPendingCustomerTransfers(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListApprovalRequestsResponse> {
  const params = new URLSearchParams();
  params.set('status', 'pending');
  params.set('kind', 'customer_transfer');
  params.set('limit', String(PENDING_TRANSFER_LIMIT));
  return opsApiRequest<ListApprovalRequestsResponse>(
    `${BASE_PATH}/mine?${params.toString()}`,
    { signal },
    fetchImpl,
  );
}

export function useMyPendingCustomerTransfers(
  enabled: boolean,
): UseQueryResult<ListApprovalRequestsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: myPendingCustomerTransfersQueryKey(),
    queryFn: ({ signal }) => listMyPendingCustomerTransfers(fetchImpl, signal),
    enabled,
    refetchInterval: APPROVALS_POLL_INTERVAL_MS,
  });
}

/** Index open transfer requests by customer org id for row/header chips. */
export function pendingTransfersByOrgId(
  data: ListApprovalRequestsResponse | undefined,
): ReadonlyMap<string, ApprovalRequestRecord> {
  const byOrg = new Map<string, ApprovalRequestRecord>();
  for (const request of data?.requests ?? []) {
    byOrg.set(request.target_id, request);
  }
  return byOrg;
}

// ─── discount_below_sales_cost requester-side hooks (E3-P3 #1106) ─────────

export function myPendingDiscountRequestsQueryKey(): readonly [
  'approvals',
  'mine',
  'discount_below_sales_cost',
  'pending',
] {
  return ['approvals', 'mine', 'discount_below_sales_cost', 'pending'] as const;
}

/**
 * The requester's open below-sales-floor discount requests — the data
 * source for the pending-discount chip on Customer 360 SKU rows.
 * Row-level by design (`/mine`): the chip marks MY in-flight
 * submissions; the approver-side queue is the approval center.
 */
export async function listMyPendingDiscountRequests(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListApprovalRequestsResponse> {
  const params = new URLSearchParams();
  params.set('status', 'pending');
  params.set('kind', 'discount_below_sales_cost');
  params.set('limit', String(PENDING_TRANSFER_LIMIT));
  return opsApiRequest<ListApprovalRequestsResponse>(
    `${BASE_PATH}/mine?${params.toString()}`,
    { signal },
    fetchImpl,
  );
}

export function useMyPendingDiscountRequests(
  enabled: boolean,
): UseQueryResult<ListApprovalRequestsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: myPendingDiscountRequestsQueryKey(),
    queryFn: ({ signal }) => listMyPendingDiscountRequests(fetchImpl, signal),
    enabled,
    refetchInterval: APPROVALS_POLL_INTERVAL_MS,
  });
}

/**
 * Index open discount requests by their composite `orgId:skuId` target
 * for per-row pending chips (`discountBelowSalesCostTargetId` shape).
 */
export function pendingDiscountsByTargetId(
  data: ListApprovalRequestsResponse | undefined,
): ReadonlyMap<string, ApprovalRequestRecord> {
  const byTarget = new Map<string, ApprovalRequestRecord>();
  for (const request of data?.requests ?? []) {
    byTarget.set(request.target_id, request);
  }
  return byTarget;
}

export async function createCustomerTransferRequest(
  orgId: string,
  payload: CustomerTransferApprovalPayload,
  fetchImpl: typeof fetch,
): Promise<CreateApprovalRequestResponse> {
  return opsApiRequest<CreateApprovalRequestResponse>(
    BASE_PATH,
    {
      method: 'POST',
      body: {
        kind: 'customer_transfer',
        target_type: 'org',
        target_id: orgId,
        payload,
      },
    },
    fetchImpl,
  );
}

export interface CreateCustomerTransferVars {
  readonly orgId: string;
  readonly payload: CustomerTransferApprovalPayload;
}

export function useCreateCustomerTransferRequest(): UseMutationResult<
  CreateApprovalRequestResponse,
  Error,
  CreateCustomerTransferVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, payload }) =>
      createCustomerTransferRequest(orgId, payload, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: APPROVALS_ROOT_KEY });
    },
  });
}

export function transferCandidatesQueryKey(
  brand: string | undefined,
): readonly ['approvals', 'transfer-candidates', string] {
  return ['approvals', 'transfer-candidates', brand ?? ''] as const;
}

export async function listTransferCandidates(
  brand: string | undefined,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListCustomerTransferCandidatesResponse> {
  const qs =
    brand !== undefined && brand.length > 0
      ? `?brand=${encodeURIComponent(brand)}`
      : '';
  return opsApiRequest<ListCustomerTransferCandidatesResponse>(
    `${TRANSFER_CANDIDATES_PATH}${qs}`,
    { signal },
    fetchImpl,
  );
}

/**
 * `brand` is only forwarded for admin sessions (ops-api pins sales
 * sessions to their profile brand and ignores the param).
 */
export function useTransferCandidates(
  brand: string | undefined,
  enabled: boolean,
): UseQueryResult<ListCustomerTransferCandidatesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: transferCandidatesQueryKey(brand),
    queryFn: ({ signal }) => listTransferCandidates(brand, fetchImpl, signal),
    enabled,
  });
}
